import { accountStore, getStudyScope, type StudyScope } from '../lib/studyStorage';

export interface ScanHistoryItem {
  id: string;
  generationId?: string;
  status?: 'processing' | 'incomplete' | 'completed' | 'failed';
  progress?: number;
  deliveryMode?: 'progressive' | 'full_audio_fallback';
  type: 'text' | 'video';
  title: string;
  subject: string;
  createdAt: string;
  uploadType: 'PDF' | 'Image' | 'File';
}

// Production: no demo scans. Real scans populate via recordScan().
const DEMO_SCANS: ScanHistoryItem[] = [];

function loadScanHistory(owner = getStudyScope()): ScanHistoryItem[] {
  try {
    const parsed = store.read(owner);
    if (Array.isArray(parsed)) {
      // Migrate old records that had scannedImage/thumbnail fields
      return parsed.filter(s => owner.ownerId || s.type !== 'video').map((s) => ({
        id: s.id,
        generationId: typeof s.generationId === 'string' ? s.generationId : undefined,
        status: s.status === 'processing' || s.status === 'incomplete' || s.status === 'failed' ? s.status : 'completed',
        progress: typeof s.progress === 'number' ? s.progress : undefined,
        deliveryMode: s.deliveryMode === 'full_audio_fallback' || s.deliveryMode === 'progressive'
          ? s.deliveryMode : undefined,
        type: s.type,
        title: s.title,
        subject: s.subject,
        createdAt: s.createdAt,
        uploadType: s.uploadType || 'Image',
      }));
    }
  } catch { /* silently ignore */ }
  return [...DEMO_SCANS];
}

function saveScanHistory(items: ScanHistoryItem[], owner = getStudyScope()) {
  store.write(items, owner);
}

const store = accountStore<ScanHistoryItem[]>('scan_history', () => []);

export function recordScan(data: Omit<ScanHistoryItem, 'id' | 'createdAt'>, owner: StudyScope = getStudyScope()) {
  if (!owner.ownerId && data.type === 'video') return;
  const scanHistoryState = loadScanHistory(owner);
  if (data.generationId) {
    const existing = scanHistoryState.findIndex(item => item.generationId === data.generationId);
    if (existing !== -1) {
      // Completion, rename and retry are updates to one generation, not new scans.
      const previous = scanHistoryState[existing];
      const item = {
        ...previous, ...data,
        progress: data.status === 'processing'
          ? Math.max(previous.progress || 0, data.progress || 0)
          : data.progress ?? previous.progress,
      };
      scanHistoryState[existing] = item;
      saveScanHistory(scanHistoryState, owner);
      return item;
    }
  }
  const now = new Date().toISOString();
  const id = `scan-${now}-${Math.random().toString(36).slice(2, 8)}`;
  const item: ScanHistoryItem = { ...data, id, createdAt: now };
  scanHistoryState.unshift(item);
  if (scanHistoryState.length > 100) scanHistoryState.pop();
  saveScanHistory(scanHistoryState, owner);
  return item;
}

export function getScanHistory(): ScanHistoryItem[] {
  return loadScanHistory();
}

export function updateScanProgress(generationId: string, progress: number) {
  const scanHistoryState = loadScanHistory();
  const item = scanHistoryState.find(entry => entry.generationId === generationId);
  if (!item || item.status !== 'processing') return;
  const next = Math.max(item.progress || 0, Math.min(99, Math.round(progress)));
  if (item.progress === next) return;
  item.progress = next;
  saveScanHistory(scanHistoryState);
}

export function renameScan(id: string, newTitle: string): boolean {
  const scanHistoryState = loadScanHistory();
  const idx = scanHistoryState.findIndex((s) => s.id === id);
  if (idx < 0) return false;
  scanHistoryState[idx] = { ...scanHistoryState[idx], title: newTitle };
  saveScanHistory(scanHistoryState);
  return true;
}

export function clearScanHistory() {
  saveScanHistory([]);
}